import React from 'react';
import {BotFormSelectionProvider, BotRequest, BotRequestPartial} from "../BotForm";
import {ValidationResult} from "../../../../../elements/validator/component/ValidationComponent";
import {Section} from "../../../../../elements/section/Section";
import styles from "../BotForm.module.css";
import {
    DefaultValidValidator,
    ERRORS_HOLDER,
    NotEmptyListValidator,
    MultipleEmailNotEmptyValidator,
    OptionalEmailValidator,
    validateBotTypeForSelectedLocations
} from "../../../../../elements/validator/Validator";
import {ValidatedInputField} from "../../../../../elements/validator/component/ValidatedInputField";
import {
    CustomSwitchFieldWithTitle
} from "../../../../../elements/customSwitchFieldWithTitle/CustomSwitchFieldWithTitle";
import {BotType, Country, CreateBotRequestOpenAiModelEnum, VehicleDataProvider} from "../../../../../../api/generated";
import {allBotTypeOptions} from "./util/botTypeUtil";
import {ValidatedChipInputField} from '../../../../../elements/validator/component/ValidatedChipInputField';
import {useAppSelector} from "../../../../../../app/hooks";
import {getAssistantVehicleDataProvider} from "../../../../../../utils/assistantVehicleDataProvider";
import {ValidatedMultiSelectField} from "../../../../../elements/validator/component/ValidatedMultiSelectField";
import {allSupportedPhoneLocaleOptions} from "../../../phoneLocaleUtil";
import {deriveLocationsFromFranchises} from "../../../franchiseTypes";
import {DependantValidatedSelectField} from "../../../../../elements/validator/dependant/DependantValidatedSelectField";
import {CcRecipientsField} from "./CcRecipientsField";
import {ValidatedSelectField} from "../../../../../elements/validator/component/ValidatedSelectField";
import {allOpenAiModelOptions, OPEN_AI_MODEL_GPT_5_6_LUNA} from "./util/openAiModelUtil";
import {WhiteTooltip} from "../../../../../elements/tooltip/WhiteTooltip";

interface Props<T> {
    getMessage: (s: any) => any;
    updateRequest: (update: BotRequestPartial) => void;
    selectionProvider: BotFormSelectionProvider;
    updateValidationStatus: (status: ValidationResult<T>) => void,
}

export const AssistantSetUp = <T extends BotRequest>({
                                                         getMessage,
                                                         updateRequest,
                                                         selectionProvider,
                                                         updateValidationStatus
                                                     }: Props<T>) => {
    const selectedFranchises = useAppSelector(selectionProvider.franchiseList);
    const selectedLocations = deriveLocationsFromFranchises(selectedFranchises);

    const botTypeValidator = (_fieldName: string, value?: BotType) => {
        const assistantVehicleDataProvider = getAssistantVehicleDataProvider(selectedLocations);
        if (value === BotType.AimeReservation &&
            assistantVehicleDataProvider === VehicleDataProvider.KeyloopPipelineSearch) {
            return ERRORS_HOLDER.RESERVATION_PIPELINE_UNSUPPORTED;
        }

        const providerValidation = validateBotTypeForSelectedLocations(value, selectedLocations);
        if (!providerValidation.valid) {
            return providerValidation;
        }

        return ERRORS_HOLDER.VALID;
    };

    return <Section title={"Assistant Set-Up"}>
        <div className={styles.fieldsLineWithChipSelector}>
            <DependantValidatedSelectField<BotType, T>
                onChange={(v) => updateRequest({botType: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'botType'}
                selector={selectionProvider.botType}
                anchorValueSelector={selectionProvider.franchiseList}
                validator={botTypeValidator}
                label={'Assistant'}
                getMessage={getMessage}
                options={allBotTypeOptions}
            />
            <ValidatedChipInputField<T>
                onChange={(v) => updateRequest({emailTranscriptRecipients: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'emailTranscriptRecipients'}
                selector={selectionProvider.emailTranscriptRecipients}
                validator={MultipleEmailNotEmptyValidator}
                label={'Transcript Recipient(s)'}
                placeholder={"example@mail.com"}
                getMessage={getMessage}
            />
        </div>
        <div className={styles.fieldsLineWithMargin}>
            <ValidatedInputField<string, T>
                required={false}
                onChange={(v) => updateRequest({assistantEmail: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'assistantEmail'}
                selector={selectionProvider.assistantEmail}
                validator={OptionalEmailValidator}
                label={'Assistant Email'}
                placeholder={"example@mail.com"}
                getMessage={getMessage}
            />
            <ValidatedInputField<string, T>
                required={false}
                onChange={(v) => updateRequest({assistantEmailPassword: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'assistantEmailPassword'}
                selector={selectionProvider.assistantEmailPassword}
                validator={DefaultValidValidator}
                label={'Assistant Email Password'}
                placeholder={"Add password"}
                getMessage={getMessage}
            />
        </div>
        <div className={styles.fieldsLineWithMargin}>
            <CcRecipientsField<T>
                selector={selectionProvider.ccRecipients}
                updateRequest={updateRequest}
                updateValidationStatus={updateValidationStatus}
                getMessage={getMessage}
            />
        </div>
        <div className={styles.fieldsLineWithMargin}>
            <ValidatedInputField<string, T>
                onChange={(v) => updateRequest({openAiApiKey: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'openAiApiKey'}
                selector={selectionProvider.openAiApiKey}
                validator={DefaultValidValidator}
                label={'OpenAI Api Key'}
                placeholder={"sk-..."}
                getMessage={getMessage}
                required={false}
            />
            <ValidatedSelectField<CreateBotRequestOpenAiModelEnum, T>
                onChange={(v) => updateRequest({openAiModel: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'openAiModel'}
                selector={selectionProvider.openAiModel}
                validator={DefaultValidValidator}
                label={'OpenAI Model'}
                options={allOpenAiModelOptions}
                getMessage={getMessage}
                required={false}
                selectFirstOptionByDefault={false}
                isClearable
                placeholder={`Default model (${OPEN_AI_MODEL_GPT_5_6_LUNA})`}
                labelAccessory={
                    <WhiteTooltip title={<span className={styles.fieldTooltipText}>
                        If no model is selected, the default model ({OPEN_AI_MODEL_GPT_5_6_LUNA}) will be used.
                    </span>}>
                        <span className={styles.fieldTooltipIcon} aria-label="OpenAI model help" tabIndex={0}>?</span>
                    </WhiteTooltip>
                }
            />
        </div>
        <CustomSwitchFieldWithTitle
            textOnTrue={"On"}
            textOnFalse={"Off"}
            title={"Multi-Agent Flow"}
            description={"Enable handoffs-based multi-agent architecture for this bot"}
            valueSelector={selectionProvider.useMultiAgent}
            toggleValue={(v) => updateRequest({useMultiAgent: v})}
        />
        <CustomSwitchFieldWithTitle
            textOnTrue={"On"}
            textOnFalse={"Off"}
            title={"Provider Warning Emails"}
            description={"Send internal email notifications for this bot's vehicle provider warnings"}
            valueSelector={selectionProvider.vehicleProviderWarningEmailEnabled}
            toggleValue={(v) => updateRequest({vehicleProviderWarningEmailEnabled: v})}
        />
        <div className={styles.fieldsLineWithMargin}>
            <ValidatedMultiSelectField<Country, T>
                onChange={(v) => updateRequest({supportedPhoneNumberLocales: v})}
                updateValidationStatus={updateValidationStatus}
                fieldName={'supportedPhoneNumberLocales'}
                selector={selectionProvider.supportedPhoneNumberLocales}
                validator={NotEmptyListValidator}
                label={'Supported Phone Number Locales'}
                getMessage={getMessage}
                options={allSupportedPhoneLocaleOptions}
                isEqual={(a, b) => a === b}
            />
        </div>
        <CustomSwitchFieldWithTitle
            textOnTrue={"On"}
            textOnFalse={"Off"}
            title={"Transcripts Filtration"}
            description={"Only chats with a stored user email or phone number will be sent to you"}
            valueSelector={selectionProvider.filtrationEnabled}
            toggleValue={(v) => updateRequest({filtrationEnabled: v})}
        />
        <CustomSwitchFieldWithTitle
            textOnTrue={"On"}
            textOnFalse={"Off"}
            title={"Transcript Summary"}
            description={"Generate summary for transcripts when trigger tools are used"}
            valueSelector={selectionProvider.transcriptSummaryEnabled}
            toggleValue={(v) => updateRequest({transcriptSummaryEnabled: v})}
        />
        <CustomSwitchFieldWithTitle
            textOnTrue={"On"}
            textOnFalse={"Off"}
            title={"Sharing Link"}
            description={"Allow public read-only transcript links for EM2 lead summaries"}
            valueSelector={selectionProvider.sharingLinkEnabled}
            toggleValue={(v) => updateRequest({sharingLinkEnabled: v})}
        />
    </Section>
}
